import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useTrip } from '../../hooks/queries'
import { useTexte } from '../../texte'
import {
  KalenderIcon,
  LupeIcon,
  MapIcon,
  PhotoIcon,
  ReisenIcon,
  ReplyIcon,
  SprechblaseIcon,
} from '../../components/Icons'
import { Tagestrennerliste } from '../thread/Tagestrennerliste'
import { useReplyChats } from '../thread/Antworten'
import { zeigtPlaylist } from '../map/Reiseumschalter'
import { jumpBegins } from '../../lib/jumpState'
import { ChoiceMenu } from '../../components/ChoiceMenu'
import { useBreitesFenster } from '../../hooks/useBreitesFenster'

/**
 * **The new navigation, next to the old one** (#174, 1.190.0).
 *
 * From use: "so bauen, dass es neben der bisherigen Anwendung steht und
 * umgeschaltet werden kann … je Benutzer, damit man es zuerst testen kann".
 * The switch is `users.new_ui`, set only by the administration; with it off
 * every page draws exactly what it drew before. The design is the mockup N2:
 *
 * - **No bar at the top.** A suitcase, a small title and a group of glass
 *   buttons float over the content (`FloatingHead`), and the content scrolls
 *   under them. Pages leave room through `--head-height`, which the head
 *   measures itself instead of a number guessed in the stylesheet.
 * - **At the bottom one bar of views** (`ViewBar`): Chat, Karte, Medien, and
 *   for the author Antworten, with a round search button. On a phone it
 *   shrinks to one round button with the open view, beside the composer or
 *   the cards of the map; it opens on a tap or when scrolling up. On an iPad
 *   it stands in the middle of the head, always open (1.191.0, `views` of
 *   `FloatingHead`).
 *
 * Readers get no tab for answers (decided from use: "Kein Reiter"); their
 * answers are in the chat as before.
 */
export { useNewNavigation } from './useNewNavigation'

export type View = 'chat' | 'map' | 'media' | 'replies'

/**
 * Who set a height last owns it. On a change of page React draws the new
 * page before the old one unmounts, so the old cleanup would otherwise take
 * the new page's room away.
 */
const owners = new Map<string, object>()

/**
 * The height of an element at the edge of a page, as a property on the root.
 * `fromTop`: from the top of its parent to its lower edge (the head);
 * otherwise its own height (the bar at the bottom).
 */
function useHeightAsProperty(name: string | null, fromTop: boolean): RefObject<HTMLDivElement | null> {
  const ref = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    const element = ref.current
    if (element === null || name === null) return
    const me = {}
    owners.set(name, me)
    const root = document.documentElement
    const measure = () => {
      if (owners.get(name) !== me) return
      const height = fromTop ? element.offsetTop + element.offsetHeight : element.offsetHeight
      root.style.setProperty(name, `${Math.round(height)}px`)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => {
      observer.disconnect()
      if (owners.get(name) === me) {
        owners.delete(name)
        root.style.removeProperty(name)
      }
    }
  }, [name, fromTop])
  return ref
}

/**
 * The floating head: suitcase, title, a glass group (`children`), and what
 * stands on the right (`right`, the pencil of the map). `below` is a second
 * row, the choice of media.
 *
 * **On an iPad the bar of views stands in the middle of this row** (1.191.0,
 * `views`). From use: on the map the capsule lay over "Noch kein POI
 * vorhanden"; in 1.190.0 it stood absolutely in the middle of the page, over
 * whatever was there, and at 1024 points over the buttons of the head. Now
 * the row is a grid of three cells (start, middle, end), and nothing in one
 * cell can lie over another. The title moves to the start beside the
 * suitcase: "am iPad ist nicht mehr erkennbar, welche Reise ausgewählt ist".
 */
export function FloatingHead({
  title,
  children,
  right,
  below,
  views,
}: {
  title?: string
  children?: ReactNode
  right?: ReactNode
  below?: ReactNode
  /** The bar of views for the middle of the row on a wide window. */
  views?: { tripId: number; active: View }
}) {
  const t = useTexte()
  const wide = useBreitesFenster()
  const ref = useHeightAsProperty('--head-height', true)
  const withViews = wide && views !== undefined
  const heading = <h2 className="floating-head__title">{title ?? ''}</h2>
  return (
    <div className="floating-head" ref={ref} data-floating-head="">
      <div className="floating-head__row" data-with-views={withViews ? '' : undefined}>
        <div className="floating-head__start">
          <Link className="glass-button" to="/" aria-label={t.allgemein.zuDenReisen} data-nav-target="trips">
            <ReisenIcon />
          </Link>
          {withViews && heading}
        </div>
        <div className="floating-head__middle">
          {withViews ? <ViewBar tripId={views.tripId} active={views.active} place="head" open onOpen={noop} /> : heading}
        </div>
        <div className="floating-head__end">
          {children !== undefined && children !== null && children !== false && (
            <div className="glass-group">{children}</div>
          )}
          {right}
        </div>
      </div>
      {below}
    </div>
  )
}

function noop(): void {}

/** The chapters of a trip: the list of day separators, only if there is one. */
export function ChaptersButton({ tripId }: { tripId: number }) {
  const t = useTexte().newNavigation
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        className="glass-group__button"
        aria-label={t.chapters}
        title={t.chapters}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        data-nav-target="chapters"
      >
        <KalenderIcon />
      </button>
      {open && (
        <Tagestrennerliste
          tripId={tripId}
          onSchliessen={() => setOpen(false)}
          onWahl={(id) => {
            setOpen(false)
            jumpBegins()
            navigate(`/t/${tripId}?m=${id}`)
          }}
        />
      )}
    </>
  )
}

/** No step counts this long after a tap opened or closed the bar (1.191.0). */
const QUIET_AFTER_TOGGLE_MS = 400

/**
 * Nor this long after the list changed its size: the browser's own move of
 * `scrollTop` follows in the next frame.
 */
const QUIET_AFTER_RESIZE_MS = 100

/**
 * Opens on scrolling up and closes on scrolling down, like the tab bar of
 * iOS 26. Measured as a sum of the last moves in one direction, so a
 * trembling finger does not flip it. A `column-reverse` list counts its
 * `scrollTop` from the newest end and goes negative upwards; a plain list
 * goes towards zero. In both, up is a smaller number.
 *
 * **Only a move of the list counts, not a change of its size** (1.191.0).
 * From use, with a screen recording: on the iPhone the bar in the chat
 * opened and closed again and again while scrolling, and a tap opened it
 * for a moment only. In 1.190.0 the open bar made the footer taller; the
 * timeline got shorter, WebKit moved its `scrollTop` by that much (it keeps
 * the newest end of a `column-reverse` list in place), and this hook took
 * the move for a scroll down. Two barriers, each holds alone: the open bar
 * no longer changes the size of the list (it lies over it, stylesheet
 * `.new-footer`), and a step with a new `clientHeight`, within
 * `QUIET_AFTER_RESIZE_MS` of a change of size or within
 * `QUIET_AFTER_TOGGLE_MS` of a toggle starts the count afresh.
 * `newnavigation.mjs` puts WebKit's move in place, since Chromium does not
 * make it.
 */
export function useBarByScroll(
  list: RefObject<HTMLElement | null>,
  on: boolean,
): [boolean, (open: boolean) => void] {
  const [open, setOpenState] = useState(false)
  const isOpen = useRef(false)
  const toggledAt = useRef(0)
  const resizedAt = useRef(0)
  const flip = useCallback((next: boolean) => {
    if (isOpen.current === next) return false
    isOpen.current = next
    setOpenState(next)
    return true
  }, [])
  // What the page hands to the bar is a tap (the round button, a tab, a tap
  // elsewhere); a scroll that flips the bar does not make it deaf.
  const setOpen = useCallback((next: boolean) => {
    if (flip(next)) toggledAt.current = performance.now()
  }, [flip])
  useEffect(() => {
    const element = list.current
    if (!on || element === null) return
    let last = element.scrollTop
    let height = element.clientHeight
    let sum = 0
    const THRESHOLD = 40
    // The size is followed as it changes, not only read at the next scroll:
    // read then, a first real scroll after any change of layout (the head
    // measured, a stripe about the network) would be taken for one.
    const sizes = new ResizeObserver(() => {
      if (element.clientHeight === height) return
      height = element.clientHeight
      resizedAt.current = performance.now()
    })
    sizes.observe(element)
    const onScroll = () => {
      const now = element.scrollTop
      const step = now - last
      last = now
      const at = performance.now()
      if (
        element.clientHeight !== height
        || at - resizedAt.current < QUIET_AFTER_RESIZE_MS
        || at - toggledAt.current < QUIET_AFTER_TOGGLE_MS
      ) {
        sum = 0
        return
      }
      if (step === 0) return
      sum = Math.sign(step) === Math.sign(sum) ? sum + step : step
      if (sum <= -THRESHOLD) flip(true)
      else if (sum >= THRESHOLD) flip(false)
    }
    element.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      sizes.disconnect()
      element.removeEventListener('scroll', onScroll)
    }
  }, [list, on, flip])
  return [open, setOpen]
}

/**
 * The bar of views. `place` says where it stands: on a phone in the row of
 * the composer (`row`), over the map beside the cards (`map`) or alone at
 * the bottom of a page (`bottom`); on a wide window in the middle of the
 * head (`head`, always open, given by `FloatingHead`). The phone places draw
 * nothing on a wide window and the head nothing on a narrow one, so there is
 * never a second bar in the page.
 *
 * **On a phone it behaves the same everywhere** (1.191.0, from use: "das
 * Verhalten der Menükapsel ist inkonsistent"): closed when a page opens, a
 * tap on the round button opens and closes it, a tap elsewhere closes it,
 * and where a list scrolls (`useBarByScroll`) scrolling up opens it and down
 * closes it. In 1.190.0 media and answers had it always open.
 */
export function ViewBar({
  tripId,
  active,
  place,
  open,
  onOpen,
}: {
  tripId: number
  active: View
  place: 'row' | 'map' | 'bottom' | 'head'
  open: boolean
  onOpen: (open: boolean) => void
}) {
  const t = useTexte().newNavigation
  const wide = useBreitesFenster()
  const trip = useTrip(tripId)
  const author = trip.data?.role === 'sender'
  const { unread } = useReplyChats(tripId)
  const inHead = place === 'head'
  const ref = useHeightAsProperty(inHead ? null : '--bar-height', false)
  const collapsible = !inHead
  const isOpen = open || !collapsible

  // A tap anywhere else closes the open bar again; choosing a view leaves
  // the page anyway.
  useEffect(() => {
    if (!collapsible || !open || wide) return
    const onDown = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && ref.current?.contains(target)) return
      onOpen(false)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [collapsible, open, onOpen, ref, wide])

  const chat = { view: 'chat' as const, word: t.chat, to: `/t/${tripId}`, icon: <SprechblaseIcon /> }
  const views: { view: View; word: string; to: string; icon: ReactNode; count?: number }[] = [
    chat,
    { view: 'map', word: t.map, to: `/t/${tripId}/karte`, icon: <MapIcon /> },
    { view: 'media', word: t.media, to: `/t/${tripId}/fotos`, icon: <PhotoIcon /> },
  ]
  if (author) {
    views.push({ view: 'replies', word: t.replies, to: `/t/${tripId}/antworten`, icon: <ReplyIcon />, count: unread })
  }
  const current = views.find((v) => v.view === active) ?? chat

  if (wide !== inHead) return null
  return (
    <div className={`view-bar view-bar--${place}`} ref={ref} data-view-bar={isOpen ? 'open' : 'closed'}>
      {!inHead && <button
        type="button"
        className="glass-button view-bar__button"
        aria-expanded={isOpen}
        aria-label={t.show(current.word)}
        onClick={() => onOpen(!isOpen)}
        data-view-button={active}
      >
        {current.icon}
        {author && unread > 0 && active !== 'replies' && <span className="view-bar__dot" aria-hidden="true" />}
      </button>}
      <div className="view-bar__open">
        <nav className="view-bar__capsule" aria-label={t.views}>
          {views.map((v) => (
            <Link
              key={v.view}
              className="view-bar__tab"
              to={v.to}
              aria-current={v.view === active ? 'page' : undefined}
              // The word is hidden when the head is narrow (stylesheet), so the
              // name stands here too.
              aria-label={v.count !== undefined && v.count > 0 ? t.repliesUnread(v.count) : v.word}
              title={v.word}
              onClick={() => onOpen(false)}
              data-view-target={v.view}
            >
              {v.icon}
              <span className="view-bar__word">{v.word}</span>
              {v.count !== undefined && v.count > 0 && (
                <span className="view-bar__count" aria-hidden="true">{v.count}</span>
              )}
            </Link>
          ))}
        </nav>
        <Link
          className="glass-button view-bar__search"
          to={`/t/${tripId}/suche`}
          aria-label={t.search}
          title={t.search}
          data-view-target="search"
        >
          <LupeIcon />
        </Link>
      </div>
    </div>
  )
}

type Medium = 'photos' | 'playlist' | 'polls'

/**
 * Playlist, photos and videos, and polls as one view, chosen in a menu ☰
 * (1.191.0; in 1.190.0 a segmented control). From use: "Medien: Playlist /
 * Fotos-Videos / Abstimmungen: Ausklapp-Menü statt fester Pille - wie bei
 * den Kartenoptionen". The same `ChoiceMenu` as the workbench of the map;
 * a choice replaces the page in the history, as the control did.
 */
export function MediaSwitch({ tripId, active }: { tripId: number; active: Medium }) {
  const t = useTexte().newNavigation
  const trip = useTrip(tripId)
  const navigate = useNavigate()
  const to: Record<Medium, string> = {
    photos: `/t/${tripId}/fotos`,
    playlist: `/t/${tripId}/playlist`,
    polls: `/t/${tripId}/abstimmungen`,
  }
  const choices: { key: Medium; word: string }[] = []
  if (zeigtPlaylist(trip.data) || active === 'playlist') choices.push({ key: 'playlist', word: t.playlist })
  choices.push({ key: 'photos', word: t.photos }, { key: 'polls', word: t.polls })
  return (
    <nav className="media-switch" aria-label={t.mediaSwitch} data-media-switch={active}>
      <ChoiceMenu
        name="media"
        label={t.mediaSwitch}
        choices={choices}
        current={active}
        onChoose={(medium) => navigate(to[medium], { replace: true })}
        opens="down"
      />
    </nav>
  )
}
